/**
 * Study-session state — pure, so the whole loop is testable without a DOM.
 *
 * Spec §12.4: progress is evidence, not points. There are no streaks, scores or
 * confetti here; a session records what was seen and what was not yet known, and
 * that is what the progress surface later reports. Cards marked "not yet" come
 * back at the end of the deck, which is the cheapest useful form of repetition
 * and needs no scheduling model.
 */

import type { Card, Deck } from "./types";

export type Verdict = "known" | "not-yet";

export interface Session {
  order: number[]; // indices into deck.cards, in the order they will be shown
  position: number;
  flipped: boolean;
  verdicts: Record<number, Verdict>;
}

export interface SessionStats {
  total: number;
  seen: number;
  known: number;
  notYet: number;
  remaining: number;
  /** Cards answered "not yet" that are still queued after the current one. */
  comingBack: number;
  /** Distinct cards answered "not yet" at least once this session, whatever they are now. */
  cameBack: number;
  complete: boolean;
}

export function startSession(deck: Deck): Session {
  return {
    order: deck.cards.map((_, i) => i),
    position: 0,
    flipped: false,
    verdicts: {},
  };
}

export function currentCard(deck: Deck, session: Session): Card | null {
  const index = session.order[session.position];
  if (index === undefined) return null;
  return deck.cards[index] ?? null;
}

export function flip(session: Session): Session {
  return { ...session, flipped: !session.flipped };
}

/**
 * Record a verdict and advance. "not yet" re-queues the card at the end, so a
 * deck is finished only when every card has been answered "known" at least once.
 */
export function answer(session: Session, verdict: Verdict): Session {
  const index = session.order[session.position];
  if (index === undefined) return session;

  const order = verdict === "not-yet" ? [...session.order, index] : session.order;
  return {
    ...session,
    order,
    position: session.position + 1,
    flipped: false,
    verdicts: { ...session.verdicts, [index]: verdict },
  };
}

export function stats(deck: Deck, session: Session): SessionStats {
  const total = deck.cards.length;
  const verdicts = Object.values(session.verdicts);
  const known = deck.cards.filter((_, i) => session.verdicts[i] === "known").length;
  const notYet = deck.cards.filter((_, i) => session.verdicts[i] === "not-yet").length;
  return {
    total,
    seen: verdicts.length,
    known,
    notYet,
    remaining: Math.max(0, session.order.length - session.position),
    // Re-queued cards sit past the deck's first run through; count those still ahead.
    // Each "not yet" appends one entry past the first run through, so these are the cards
    // that came back — a verdict alone forgets it once the card is known.
    cameBack: new Set(session.order.slice(total)).size,
    comingBack: session.order.slice(Math.max(total, session.position + 1)).length,
    complete: session.position >= session.order.length,
  };
}
